/**
 * A2UI Renderer
 *
 * Renders A2UI flat component lists into React component trees.
 * Uses adjacency list model: components reference children by ID.
 */

import React from 'react';
import type { A2UIComponent, A2UIResponse, A2UIRenderContext } from '../types/a2ui';
import { a2uiCatalog } from './a2ui-catalog';

/**
 * Get a nested value from an object using dot notation path
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || !path) return undefined;

  return path.split('.').reduce((acc: unknown, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * Resolve data bindings against the data model
 */
function resolveDataBindings(
  props: Record<string, unknown> | undefined,
  bindings: Record<string, string> | undefined,
  data: Record<string, unknown>,
  itemContext?: unknown
): Record<string, unknown> {
  const resolved = { ...props };

  if (!bindings) return resolved;

  for (const [propName, path] of Object.entries(bindings)) {
    if (path.startsWith('$item.')) {
      // Resolve from iteration context
      const itemPath = path.slice(6); // Remove "$item."
      resolved[propName] = getNestedValue(itemContext, itemPath);
    } else if (path === '$item') {
      // Direct item reference
      resolved[propName] = itemContext;
    } else {
      // Resolve from root data model
      resolved[propName] = getNestedValue(data, path);
    }
  }

  return resolved;
}

/**
 * Error component for rendering errors
 */
function RenderError({ message, componentId }: { message: string; componentId?: string }) {
  return (
    <div className="p-4 bg-coral/10 border border-coral text-coral">
      <p className="font-mono text-xs uppercase tracking-wider mb-1">Render Error</p>
      <p className="text-sm">{message}</p>
      {componentId && (
        <p className="text-xs text-grey-medium mt-1">Component ID: {componentId}</p>
      )}
    </div>
  );
}

/**
 * Render a single component by ID
 */
function renderComponent(
  id: string,
  context: A2UIRenderContext,
  visited: Set<string> = new Set()
): React.ReactNode {
  // Circular reference detection
  if (visited.has(id)) {
    console.warn(`[A2UI] Circular reference detected for component: ${id}`);
    return <RenderError message={`Circular reference: ${id}`} componentId={id} />;
  }

  const component = context.componentMap.get(id);
  if (!component) {
    console.warn(`[A2UI] Component not found: ${id}`);
    return <RenderError message={`Component not found: ${id}`} componentId={id} />;
  }

  // Get the React component from catalog
  const Component = a2uiCatalog[component.component];
  if (!Component) {
    console.warn(`[A2UI] Unknown component type: ${component.component}`);
    return (
      <RenderError
        message={`Unknown component type: ${component.component}`}
        componentId={id}
      />
    );
  }

  // Resolve data bindings
  const resolvedProps = resolveDataBindings(
    component.props,
    component.dataBindings,
    context.data,
    context.itemContext
  );

  // Track visited for circular reference detection
  const newVisited = new Set(visited);
  newVisited.add(id);

  // Build children
  let children: React.ReactNode = null;

  if (component.text) {
    // Simple text content
    children = component.text;
  } else if (component.childrenFromData) {
    // Dynamic children from data array
    const items = getNestedValue(context.data, component.childrenFromData.dataPath);

    if (Array.isArray(items)) {
      children = items.map((item, index) => {
        const childContext: A2UIRenderContext = {
          ...context,
          itemContext: item,
          itemIndex: index,
        };
        return (
          <React.Fragment key={index}>
            {renderComponent(component.childrenFromData!.templateId, childContext, new Set())}
          </React.Fragment>
        );
      });
    } else {
      console.warn(`[A2UI] childrenFromData path "${component.childrenFromData.dataPath}" did not resolve to an array`);
    }
  } else if (component.children && component.children.length > 0) {
    // Static children by ID reference
    children = component.children.map((childId) => (
      <React.Fragment key={childId}>
        {renderComponent(childId, context, newVisited)}
      </React.Fragment>
    ));
  }

  // Render the component
  try {
    return <Component {...resolvedProps}>{children}</Component>;
  } catch (error) {
    console.error(`[A2UI] Error rendering component ${id}:`, error);
    return (
      <RenderError
        message={error instanceof Error ? error.message : 'Unknown error'}
        componentId={id}
      />
    );
  }
}

/**
 * Build a component map from flat component list
 */
function buildComponentMap(components: A2UIComponent[]): Map<string, A2UIComponent> {
  const map = new Map<string, A2UIComponent>();

  for (const component of components) {
    if (map.has(component.id)) {
      console.warn(`[A2UI] Duplicate component ID: ${component.id}`);
    }
    map.set(component.id, component);
  }

  return map;
}

/**
 * Main A2UI Renderer Component
 */
export interface A2UIRendererProps {
  /** A2UI response containing flat component list */
  response: A2UIResponse;
  /** Data model for data bindings (MCP query results) */
  data?: Record<string, unknown>;
  /** Optional className for wrapper */
  className?: string;
}

export function A2UIRenderer({ response, data = {}, className }: A2UIRendererProps) {
  // Validate response
  if (!response || !response.components || !Array.isArray(response.components)) {
    return (
      <div className={className}>
        <RenderError message="Invalid A2UI response: missing components array" />
      </div>
    );
  }

  if (response.components.length === 0) {
    return (
      <div className={className}>
        <RenderError message="Empty A2UI response: no components" />
      </div>
    );
  }

  // Build component map
  const componentMap = buildComponentMap(response.components);

  // Determine root ID
  const rootId = response.rootId || 'root';

  if (!componentMap.has(rootId)) {
    // Try to find a component named "root" or use the first component
    const fallbackRoot = componentMap.has('root') ? 'root' : response.components[0]?.id;

    if (!fallbackRoot) {
      return (
        <div className={className}>
          <RenderError message={`Root component "${rootId}" not found`} />
        </div>
      );
    }

    console.warn(`[A2UI] Root "${rootId}" not found, using "${fallbackRoot}"`);
  }

  // Create render context
  const context: A2UIRenderContext = {
    componentMap,
    data,
  };

  // Render from root
  return (
    <div className={className}>
      {renderComponent(componentMap.has(rootId) ? rootId : (response.rootId || response.components[0]?.id || 'root'), context)}
    </div>
  );
}

export default A2UIRenderer;
